import { useState, useCallback } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	MediaUploadPermissionResponse,
	UploadContext,
	UseUploadPermissions,
} from './types.tsx';

export const useUploadPermissions: UseUploadPermissions = () => {
	const [uploadPermissions, setUploadPermissions] = useState<{
		loading: boolean;
		error: boolean;
		permissions: UploadContext | null;
	}>({ loading: false, error: false, permissions: null });
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fetchUploadPermissions = useCallback(
		async (issueId: number): Promise<void> => {
			setUploadPermissions((currentState) => ({
				...currentState,
				loading: true,
			}));

			try {
				const uploadResponse: MediaUploadPermissionResponse = await fetchJson(
					`/rest/api/2/attachment/upload/issue/${issueId}/credentials`,
				);
				setUploadPermissions({
					loading: false,
					error: false,
					permissions: {
						clientId: uploadResponse.clientId,
						serviceHost: uploadResponse.endpointUrl,
						token: uploadResponse.token,
						tokenLifespanInMs: uploadResponse.tokenDurationInMins * 60 * 1000,
						collection: uploadResponse.targetCollection,
						tokenIssueTimestamp: Date.now() - 10000,
					},
				});
				fireOperationalAnalytics(
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						action: 'success',
						actionSubject: 'fetchUploadPermissions',
					}),
					'fetchUploadPermissions success',
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				fireErrorAnalytics({
					error: e,
					meta: {
						id: 'fetchUploadPermissions',
						packageName: 'jiraIssueNavigatorIssueOperationsServices',
						teamName: 'empanada',
					},
				});
				setUploadPermissions({
					permissions: null,
					loading: false,
					error: true,
				});
			}
		},
		[createAnalyticsEvent],
	);
	return { uploadPermissions, fetchUploadPermissions };
};

export default useUploadPermissions;
